import { type Editor, Extension, InputRule, PasteRule } from '@tiptap/core'
import { Placeholder } from '@tiptap/extensions'
import { MarkdownManager } from '@tiptap/markdown'
import { Plugin } from '@tiptap/pm/state'
import { EditorContent, useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useI18n } from '@/i18n/i18n'
import { isComposing } from '@/lib/ime'
import { Latest } from '@/lib/latest'
import { materialText, mentionAllowed, mentionItems } from './cardEditor/mentions'
import { SuggestStore, suggestExtension } from './cardEditor/suggest'
import { SuggestList } from './cardEditor/SuggestList'
import { useBoardCtx } from './context'

// Writing a message on the board, or rewriting one's own before it is handed
// to the AI. Rich text, kept as markdown like the AI's messages; `@` points at
// things on the board as in a card. Enter places it, ⌘/Ctrl+Enter places it
// and hands it to the AI, Shift+Enter starts a new line; Esc, or leaving it
// empty, cancels. While an IME is composing, keys belong to the IME.

/**
 * `[words](address)` typed or pasted becomes a link reading "words", as the AI
 * writes them; `![…](…)` too (an embed belongs in the chat, so a link here).
 */
const MARKDOWN_LINK = /!?\[([^\]]+)\]\(([^)\s]+)\)$/
const MarkdownLinks = Extension.create({
  name: 'markdownLinks',
  addInputRules() {
    return [
      new InputRule({
        find: MARKDOWN_LINK,
        handler: ({ state, range, match }) => {
          const [, words = '', href = ''] = match
          const link = state.schema.marks.link
          if (!link) return
          state.tr.replaceWith(
            range.from,
            range.to,
            state.schema.text(words, [link.create({ href })]),
          )
          state.tr.removeStoredMark(link)
        },
      }),
    ]
  },
  addPasteRules() {
    return [
      new PasteRule({
        find: /!?\[([^\]]+)\]\(([^)\s]+)\)/g,
        handler: ({ state, range, match }) => {
          const [, words = '', href = ''] = match
          const link = state.schema.marks.link
          if (!link) return
          state.tr.replaceWith(
            range.from,
            range.to,
            state.schema.text(words, [link.create({ href })]),
          )
        },
      }),
    ]
  },
})

function messageExtensions() {
  return [
    MarkdownLinks,
    StarterKit.configure({
      heading: false,
      horizontalRule: false,
      trailingNode: false,
      link: { protocols: ['item', 'edge', 'region', 'mat'], openOnClick: false },
    }),
  ]
}

const markdown = new MarkdownManager({ extensions: messageExtensions() })

/** The message as markdown; `&`, `<` and `>` as typed (the serializer writes entities). */
function toMarkdown(editor: Editor): string {
  return markdown
    .serialize(editor.getJSON())
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trim()
}

type Keys = { submit: (handToAi: boolean) => void; cancel: () => void }

/** Enter, ⌘Enter and Esc: after the `@` list has had its turn, before the editor's own Enter. */
function submitKeys(keys: { current: Keys }) {
  return Extension.create({
    name: 'messageKeys',
    priority: 500,
    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handleKeyDown(_view, e) {
              if (isComposing(e)) return false
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                keys.current.submit(e.metaKey || e.ctrlKey)
                return true
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                keys.current.cancel()
                return true
              }
              return false
            },
          },
        }),
      ]
    },
  })
}

export function MessageComposer({
  width,
  placeholder,
  initial = '',
  onSubmit,
  onCancel,
}: {
  width: number
  /** What is being written (a reply, something about…); defaults to a thought. */
  placeholder?: string
  /** The words being rewritten, as markdown. */
  initial?: string
  onSubmit: (text: string, handToAi: boolean) => void
  onCancel: () => void
}) {
  const { t } = useI18n()
  const { client, boardId } = useBoardCtx()
  const label = placeholder ?? t('composer.placeholder')
  const root = useRef<HTMLDivElement>(null)
  const [mention] = useState(() => new SuggestStore())
  const [keys] = useState(
    () => new Latest<Keys>({ submit: () => undefined, cancel: () => undefined }),
  )
  const [tNow] = useState(() => new Latest(t))
  useLayoutEffect(() => {
    tNow.set(t)
  }, [t, tNow])

  const editor = useEditor({
    extensions: [
      ...messageExtensions(),
      Placeholder.configure({ placeholder: label }),
      suggestExtension({
        char: '@',
        name: 'mentionSuggest',
        store: mention,
        allowSpaces: true,
        allowAfter: mentionAllowed,
        label: () => tNow.current('mention.label'),
        empty: () => tNow.current('mention.empty'),
        items: (query) =>
          mentionItems(
            {
              view: () => client.view,
              doc: client.doc,
              materials: () => client.getMaterials(),
              text: (m) => materialText(boardId, m),
              self: '',
              t: (k, v) => tNow.current(k, v),
            },
            query,
          ),
      }),
      submitKeys(keys),
    ],
    content: initial ? markdown.parse(initial) : '',
    // The editor exists on the first render, so it can take focus at once.
    immediatelyRender: true,
    editorProps: {
      attributes: {
        class:
          'say-prose min-h-[1.6em] text-[13.5px] leading-relaxed text-ink outline-none [&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-muted [&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
      },
    },
  })

  // In the same task that opened it (the editor's DOM is attached by now), as
  // a text field would: keys typed right after a double-click must land here,
  // not on the board. Tiptap's focus command waits a frame, which under load
  // let the first keys go elsewhere: focus the view itself.
  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.view.focus()
    editor.commands.setTextSelection(editor.state.doc.content.size)
  }, [editor])

  useEffect(() => {
    if (!editor) return
    // Enter places it and the composer goes away, which blurs the editor: once.
    let done = false
    const finish = (handToAi: boolean) => {
      if (done) return
      done = true
      const text = toMarkdown(editor)
      if (text && text !== initial.trim()) onSubmit(text, handToAi)
      else if (text && handToAi) onSubmit(text, true)
      else onCancel()
    }
    keys.set({
      submit: finish,
      cancel: () => {
        done = true
        onCancel()
      },
    })
    // Leaving it places or cancels it. Only a real leave counts: the editor
    // can blur for a moment while it is set up (rebuilt in development), and
    // focus returns at once; the `@` list never takes focus.
    let check: ReturnType<typeof setTimeout> | undefined
    const onBlur = () => {
      clearTimeout(check)
      check = setTimeout(() => {
        if (editor.isDestroyed || editor.isFocused) return
        if (root.current?.contains(document.activeElement)) return
        finish(false)
      }, 0)
    }
    editor.on('blur', onBlur)
    return () => {
      clearTimeout(check)
      editor.off('blur', onBlur)
    }
  }, [editor, keys, initial, onSubmit, onCancel])

  return (
    <div
      ref={root}
      className="nodrag nopan nowheel rounded-[4px_14px_14px_14px] border border-(--focus) bg-(--note) px-3 py-2 shadow-lg"
      style={{ width }}
    >
      <EditorContent editor={editor} />
      <div className="mt-1 text-[11px] text-muted">{t('composer.hint')}</div>
      <SuggestList store={mention} />
    </div>
  )
}
